import { GenericError } from "@auth0/auth0-spa-js";
import { StytchAPIError } from "@stytch/react";
import { ClientError } from "graphql-request";

import { SessionHydrationError } from "@/lib/auth/session-hydration-error";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";

/**
 * Telemetria dos fluxos de autenticação via Stytch (login, primeiro acesso e migração da
 * sessão do Auth0).
 *
 * Dois sinais são emitidos a partir daqui:
 * - RudderStack (`trackEvent`) → BigQuery: alimenta os alertas de QUEDA de volume/conversão.
 * - Sentry (`captureAuthError`): captura FALHAS com tags padronizadas para os alertas de erro.
 *
 * Nomes de evento e propriedades seguem a convenção do Discovery (pt-BR, Title Case).
 * Os nomes de tag do Sentry usam o namespace `auth.*` para filtragem nas regras de alerta.
 *
 * Ver `docs/analytics/stytch-auth-events.md` para o catálogo de eventos e as definições de alerta.
 */

const PROVIDER_STYTCH = "stytch";

export type LoginMethod = "senha" | "google" | "apple" | "social";
export type AuthResult = "sucesso" | "falha";
type AuthFlow = "login" | "primeiro-acesso" | "migracao-sessao";

/** Etapas do funil de primeiro acesso, na ordem em que o usuário as percorre. */
export type FirstAccessStep = "documento" | "verificacao-email" | "definir-senha";

const FIRST_ACCESS_STEP_NUMBER: Record<FirstAccessStep, number> = {
  documento: 1,
  "verificacao-email": 2,
  "definir-senha": 3,
};

const AuthEvent = {
  LoginStarted: "Login Iniciado",
  LoginSucceeded: "Login Concluído",
  LoginFailed: "Login Falhou",
  FirstAccessStarted: "Primeiro Acesso Iniciado",
  FirstAccessStepViewed: "Primeiro Acesso Etapa Visualizada",
  FirstAccessDocumentNotFound: "Primeiro Acesso Documento Não Encontrado",
  FirstAccessDocumentDuplicate: "Primeiro Acesso Documento Duplicado",
  FirstAccessOtpSent: "Primeiro Acesso OTP Enviado",
  FirstAccessOtpSendFailed: "Primeiro Acesso OTP Falhou Envio",
  FirstAccessOtpVerified: "Primeiro Acesso OTP Verificado",
  FirstAccessOtpResent: "Primeiro Acesso OTP Reenviado",
  FirstAccessPasswordSet: "Primeiro Acesso Senha Definida",
  FirstAccessCompleted: "Primeiro Acesso Concluído",
  SessionMigrationStarted: "Migração de Sessão Iniciada",
  SessionMigrationSucceeded: "Migração de Sessão Concluída",
  SessionMigrationFailed: "Migração de Sessão Falhou",
} as const;

/** Extrai o `error_type` de um StytchAPIError; `undefined` para qualquer outro erro. */
const getStytchErrorType = (error: unknown): string | undefined =>
  error instanceof StytchAPIError ? error.error_type : undefined;

// --- Login -----------------------------------------------------------------

export const trackLoginStarted = (metodo: LoginMethod): void => {
  trackEvent(AuthEvent.LoginStarted, { provedor: PROVIDER_STYTCH, metodo });
};

export const trackLoginSucceeded = (metodo: LoginMethod): void => {
  trackEvent(AuthEvent.LoginSucceeded, { provedor: PROVIDER_STYTCH, metodo });
};

export const trackLoginFailed = (metodo: LoginMethod, error: unknown): void => {
  const tipoErro = getStytchErrorType(error);

  trackEvent(AuthEvent.LoginFailed, {
    provedor: PROVIDER_STYTCH,
    metodo,
    ...(tipoErro ? { tipoErro } : {}),
  });
};

// --- Primeiro acesso -------------------------------------------------------

export const trackFirstAccessStarted = (): void => {
  trackEvent(AuthEvent.FirstAccessStarted, { provedor: PROVIDER_STYTCH });
};

export const trackFirstAccessStepViewed = (etapa: FirstAccessStep): void => {
  trackEvent(AuthEvent.FirstAccessStepViewed, {
    provedor: PROVIDER_STYTCH,
    etapa,
    numeroEtapa: FIRST_ACCESS_STEP_NUMBER[etapa],
  });
};

/** CPF informado não foi encontrado na base — usuário não consegue prosseguir. */
export const trackFirstAccessDocumentNotFound = (): void => {
  trackEvent(AuthEvent.FirstAccessDocumentNotFound, { provedor: PROVIDER_STYTCH });
};

/** CPF informado retornou múltiplos usuários (duplicidade na base). */
export const trackFirstAccessDocumentDuplicate = (): void => {
  trackEvent(AuthEvent.FirstAccessDocumentDuplicate, { provedor: PROVIDER_STYTCH });
};

export const trackFirstAccessOtpSent = (): void => {
  trackEvent(AuthEvent.FirstAccessOtpSent, { provedor: PROVIDER_STYTCH });
};

export const trackFirstAccessOtpSendFailed = (error: unknown): void => {
  const tipoErro = getStytchErrorType(error);

  trackEvent(AuthEvent.FirstAccessOtpSendFailed, {
    provedor: PROVIDER_STYTCH,
    ...(tipoErro ? { tipoErro } : {}),
  });
};

export const trackFirstAccessOtpResent = (): void => {
  trackEvent(AuthEvent.FirstAccessOtpResent, { provedor: PROVIDER_STYTCH });
};

export const trackFirstAccessOtpVerified = (
  resultado: AuthResult,
  error?: unknown,
): void => {
  const tipoErro = getStytchErrorType(error);

  trackEvent(AuthEvent.FirstAccessOtpVerified, {
    provedor: PROVIDER_STYTCH,
    resultado,
    ...(tipoErro ? { tipoErro } : {}),
  });
};

export const trackFirstAccessPasswordSet = (
  resultado: AuthResult,
  error?: unknown,
): void => {
  const tipoErro = getStytchErrorType(error);

  trackEvent(AuthEvent.FirstAccessPasswordSet, {
    provedor: PROVIDER_STYTCH,
    resultado,
    ...(tipoErro ? { tipoErro } : {}),
  });
};

export const trackFirstAccessCompleted = (): void => {
  trackEvent(AuthEvent.FirstAccessCompleted, { provedor: PROVIDER_STYTCH });
};

// --- Migração da sessão do Auth0 -------------------------------------------

const SESSION_MIGRATION_PROPERTIES = { provedor: PROVIDER_STYTCH, origem: "auth0" };

/**
 * Tipo do erro da migração: o `error_type` do Stytch, que o authus devolve como mensagem
 * do erro GraphQL da action `AuthusMigrateSession`, o código do Auth0 quando falha a
 * obtenção do token do Auth0, ou `stytch_hydrate_failed` quando a sessão criada não carrega.
 * `undefined` para qualquer outro erro.
 */
const getSessionMigrationErrorType = (error: unknown): string | undefined => {
  if (error instanceof ClientError) return error.response.errors?.[0]?.message;
  if (error instanceof GenericError) return error.error;
  if (error instanceof SessionHydrationError) return error.errorType;
  return getStytchErrorType(error);
};

/**
 * A `message` do `ClientError` serializa a requisição com as variáveis — aqui, o access token
 * do Auth0. Ao Sentry vai um erro novo só com o tipo, sem `message`, `request` ou `cause` do
 * original.
 */
const toReportableSessionMigrationError = (
  error: unknown,
  errorType: string | undefined,
): unknown =>
  error instanceof ClientError
    ? new Error(`Falha na migração da sessão (${errorType ?? "desconhecido"})`)
    : error;

export const trackSessionMigrationStarted = (): void => {
  trackEvent(AuthEvent.SessionMigrationStarted, SESSION_MIGRATION_PROPERTIES);
};

export const trackSessionMigrationSucceeded = (): void => {
  trackEvent(AuthEvent.SessionMigrationSucceeded, SESSION_MIGRATION_PROPERTIES);
};

export const trackSessionMigrationFailed = (error: unknown): void => {
  const tipoErro = getSessionMigrationErrorType(error);

  trackEvent(AuthEvent.SessionMigrationFailed, {
    ...SESSION_MIGRATION_PROPERTIES,
    ...(tipoErro ? { tipoErro } : {}),
  });
};

/**
 * Captura no Sentry uma falha da migração silenciosa da sessão do Auth0 para o Stytch.
 * Roda antes de o usuário da Livance ser carregado, por isso identifica pelo `sub` do Auth0.
 */
export const captureSessionMigrationError = (
  error: unknown,
  auth0Sub: string | undefined,
): void => {
  const errorType = getSessionMigrationErrorType(error);

  logError(toReportableSessionMigrationError(error, errorType), {
    tags: {
      "auth.provider": PROVIDER_STYTCH,
      "auth.flow": "migracao-sessao" satisfies AuthFlow,
      "auth.outcome": "failure",
      ...(errorType ? { "auth.error_type": errorType } : {}),
    },
    ...(auth0Sub ? { user: { username: auth0Sub } } : {}),
  });
};

// --- Sentry ----------------------------------------------------------------

interface CaptureAuthErrorContext {
  flow: AuthFlow;
  step?: FirstAccessStep;
  method?: LoginMethod;
}

/**
 * Captura uma falha de autenticação no Sentry com tags padronizadas.
 *
 * As regras de alerta de falha filtram por `auth.provider:stytch AND auth.outcome:failure`,
 * podendo refinar por `auth.flow`, `auth.step`, `auth.method` e `auth.error_type`.
 */
export const captureAuthError = (
  error: unknown,
  { flow, step, method }: CaptureAuthErrorContext,
): void => {
  const errorType = getStytchErrorType(error);

  logError(error, {
    tags: {
      "auth.provider": PROVIDER_STYTCH,
      "auth.flow": flow,
      "auth.outcome": "failure",
      ...(step ? { "auth.step": step } : {}),
      ...(method ? { "auth.method": method } : {}),
      ...(errorType ? { "auth.error_type": errorType } : {}),
    },
  });
};
